import {useState, type ReactNode} from 'react';
import {Plus, Trash2, ChevronUp, ChevronDown, ChevronRight} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {PAY_PERIODS, sitePayPeriod, type PayPeriodInfo} from '@common/enrollmentSite/payPeriod';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@common-web/components/ui/dropdown-menu';
import {
  TextField,
  TextArea,
  StringListEditor,
  ColorSwatchField,
  Field,
  type SectionProps,
} from '../controls';
import {useMirroredField} from '../mirrorLock';
import {PdfUploadField} from '../PdfUploadField';
import {trpc} from '@common-web/lib/trpc';
import {
  rankDetailRowDefaults,
  type RankedDetailRowDefault,
} from '@common/enrollmentSite/detailRowDefaults';
import {PLAN_ACCENT_COLOR_POOL} from '@client/pages/public/enroll/coverageIcons';
import {
  COVERAGE_TIER_LABELS,
  BENEFIT_CATEGORY_LABELS,
  BENEFIT_CATEGORY_COLORS,
  type BenefitRow,
  type CoverageTier,
  type PayPeriod,
  type PlanConfig,
  type ProductConfig,
  type ProductCategory,
  type DetailRow,
} from '@common/enrollmentSite/types';

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  medical: 'Medical',
  'limited-medical': 'Limited medical',
  dental: 'Dental',
  vision: 'Vision',
  life: 'Life',
  disability: 'Disability',
  'critical-illness': 'Critical illness',
};

// Canonical flow order. Products are stored sorted by this, so the enrollee steps
// render in a fixed order no matter what order an author fills the scaffold.
const CATEGORY_ORDER: ProductCategory[] = [
  'medical',
  'limited-medical',
  'dental',
  'vision',
  'critical-illness',
  'disability',
  'life',
];

// Products on the Medical step; a site needs at least one. Limited medical shows under Medical
// when both exist, or is the step itself when there's no regular medical.
const CORE_CATEGORIES: ProductCategory[] = ['medical', 'limited-medical'];
// Steps with a single product each, in flow order.
const STANDALONE_SLOTS: ProductCategory[] = ['dental', 'vision'];
// Products that live inside the single combined "Extra Protection" step.
const PROTECTION_MEMBERS: ProductCategory[] = ['critical-illness', 'disability', 'life'];
const OPTIONAL_TABLE_CATEGORIES: ProductCategory[] = ['limited-medical', 'dental', 'vision'];

const TIERS = Object.keys(COVERAGE_TIER_LABELS) as CoverageTier[];

const BENEFIT_CATEGORIES = Object.entries(BENEFIT_CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

// Hidden, immutable plan id generated at creation — the internal key products and
// the e123 mapping join on. Never shown to authors.
function genPlanId(taken: Set<string>): string {
  let id: string;
  do {
    id = `plan-${Math.random().toString(36).slice(2, 8)}`;
  } while (taken.has(id));
  return id;
}

// A category's flow placement is fixed by the catalog, not chosen by the author.
function placementFor(category: ProductCategory): ProductConfig['inlineWith'] | undefined {
  if (category === 'limited-medical') return 'medical';
  if (PROTECTION_MEMBERS.includes(category)) return 'protection';
  return undefined; // medical, dental, vision render as their own step
}

// Keep products in canonical order with the placement their category dictates, so
// the stored config can't drift into a shape the enroll render can't display.
function normalize(list: ProductConfig[]): ProductConfig[] {
  const sorted = [...list].sort(
    (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category),
  );
  return sorted.map((p) => {
    const want = placementFor(p.category);
    if (want === undefined) {
      if (p.inlineWith === undefined) return p;
      const {inlineWith: _drop, ...rest} = p;
      return rest;
    }
    return p.inlineWith === want ? p : {...p, inlineWith: want};
  });
}

export function ProductsSection({config, patch, e123, patchE123, groupSlug, isNew}: SectionProps) {
  const payPeriod = sitePayPeriod(config);
  const {data: detailRowDefaults} = trpc.internal.enrollmentSite.detailRowDefaults.useQuery();
  const products = config.products ?? [];
  const plans = config.plans ?? [];
  const planById = new Map(plans.map((p) => [p.id, p]));
  const productByCat = new Map(products.map((p) => [p.category, p]));
  const {isMirrored, sourceName} = useMirroredField();
  const productLocked = (category: ProductCategory) => isMirrored(`product:${category}`);
  const ratesFollowSource = isMirrored('rates');
  // The plan just added via "Add plan" — its editor opens expanded so it can be filled in.
  const [expandPlanId, setExpandPlanId] = useState<string | null>(null);
  // Open the supplement's what's-covered table when it's freshly added.
  const [openSupplementTable, setOpenSupplementTable] = useState(false);

  function commit(nextProducts: ProductConfig[], nextPlans: PlanConfig[]) {
    patch({products: normalize(nextProducts), plans: nextPlans});
  }

  function addProduct(category: ProductCategory) {
    commit(
      [
        ...products,
        // Medical defaults to a recorded declination (locked); others start with no waiver prompt.
        {category, label: CATEGORY_LABELS[category], description: '', planIds: [], declinable: category === 'medical'},
      ],
      plans,
    );
  }
  function removeProduct(category: ProductCategory) {
    const product = productByCat.get(category);
    if (!product) return;
    const removedIds = new Set(product.planIds);
    commit(
      products.filter((p) => p.category !== category),
      plans.filter((pl) => !removedIds.has(pl.id)),
    );
  }
  function updateProduct(category: ProductCategory, partial: Partial<ProductConfig>) {
    commit(products.map((p) => (p.category === category ? {...p, ...partial} : p)), plans);
  }

  function addPlan(category: ProductCategory) {
    if (!productByCat.has(category)) return;
    const id = genPlanId(new Set(plans.map((p) => p.id)));
    const plan: PlanConfig = {
      id,
      category,
      name: '',
      highlights: [],
      rates: {employee: 0, employeeSpouse: 0, employeeChildren: 0, family: 0},
      accentColor: PLAN_ACCENT_COLOR_POOL[0].value,
    };
    setExpandPlanId(id);
    commit(
      products.map((p) => (p.category === category ? {...p, planIds: [...p.planIds, id]} : p)),
      [...plans, plan],
    );
  }
  function updatePlan(planId: string, partial: Partial<PlanConfig>) {
    commit(products, plans.map((pl) => (pl.id === planId ? {...pl, ...partial} : pl)));
  }
  function removePlan(category: ProductCategory, planId: string) {
    commit(
      products.map((p) =>
        p.category === category ? {...p, planIds: p.planIds.filter((id) => id !== planId)} : p,
      ),
      plans.filter((pl) => pl.id !== planId),
    );
  }
  function movePlan(category: ProductCategory, idx: number, dir: -1 | 1) {
    const product = productByCat.get(category);
    if (!product) return;
    const ids = [...product.planIds];
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    commit(products.map((p) => (p.category === category ? {...p, planIds: ids} : p)), plans);
  }

  function renderPlans(category: ProductCategory, tiered: boolean) {
    const product = productByCat.get(category);
    if (!product) return null;
    const productPlans = product.planIds
      .map((id) => planById.get(id))
      .filter((p): p is PlanConfig => !!p);
    const locked = productLocked(category);
    return (
      <Field label='Plans' hintTop hint='The options members choose within this product.'>
        <div className='space-y-3'>
          {productPlans.map((plan, idx) => (
            <PlanEditor
              key={plan.id}
              plan={plan}
              tiered={tiered}
              defaultOpen={plan.id === expandPlanId}
              isFirst={idx === 0}
              isLast={idx === productPlans.length - 1}
              groupSlug={groupSlug}
              isNew={isNew}
              onChange={(partial) => updatePlan(plan.id, partial)}
              onRemove={() => removePlan(category, plan.id)}
              onMove={(dir) => movePlan(category, idx, dir)}
              locked={locked}
              ratesLocked={locked && ratesFollowSource}
              payPeriod={payPeriod}
              showDetailRows={product.inlineWith === 'protection'}
              detailRowSources={rankDetailRowDefaults(
                detailRowDefaults ?? [],
                {id: plan.id, name: plan.name, pdid: e123.planPdid[plan.id]},
                groupSlug,
              )}
            />
          ))}
          <Button variant='outline' size='sm' onClick={() => addPlan(category)} disabled={locked}>
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add plan
          </Button>
        </div>
      </Field>
    );
  }

  function renderEditor(category: ProductCategory) {
    const product = productByCat.get(category);
    if (!product) return null;
    // Medical is always tiered; every other product can be employee-only.
    const tierLocked = category === 'medical';
    // Medical must record an explicit declination (ACA) — its waiver treatment is locked.
    const waiveLocked = category === 'medical';
    const tiered = product.tierSelectable !== false;
    const locked = productLocked(category);
    return (
      <div className='space-y-4'>
        {locked && (
          <p className='text-xs text-muted-foreground'>
            Mirrored from {sourceName}.{' '}
            {ratesFollowSource ? 'Rates follow it too.' : 'Rates are this site’s own.'}
          </p>
        )}
        <fieldset disabled={locked} className={cn('m-0 min-w-0 border-0 p-0', locked && 'opacity-60')}>
          <ProductEditor
            product={product}
            tierLocked={tierLocked}
            waiveLocked={waiveLocked}
            badgeToggle={!PROTECTION_MEMBERS.includes(category)}
            onChange={(partial) => updateProduct(category, partial)}
          />
        </fieldset>
        {renderPlans(category, tiered)}
        <fieldset disabled={locked} className={cn('m-0 min-w-0 border-0 p-0', locked && 'opacity-60')}>
          {renderBenefitsTable(category)}
        </fieldset>
      </div>
    );
  }

  // The compare-benefits table is only meaningful for medical (multi-plan comparison)
  // and the limited-medical supplement ("what's covered"); other steps don't show it.
  function renderBenefitsTable(category: ProductCategory) {
    const product = productByCat.get(category);
    if (!product) return null;
    if (category !== 'medical' && !OPTIONAL_TABLE_CATEGORIES.includes(category)) return null;
    const productPlans = product.planIds
      .map((id) => planById.get(id))
      .filter((p): p is PlanConfig => !!p);
    const editor = (heading: string, opts: {defaultOpen?: boolean; onRemove?: () => void} = {}) => (
      <BenefitsTableEditor
        heading={heading}
        defaultOpen={opts.defaultOpen}
        onRemove={opts.onRemove}
        rows={product.benefitRows ?? []}
        plans={productPlans}
        onRowsChange={(benefitRows) => updateProduct(category, {benefitRows})}
      />
    );

    if (category === 'medical') {
      return editor('Compare benefits');
    }

    if (product.benefitRows === undefined) {
      return (
        <Button variant='outline' size='sm' onClick={() => {
          setOpenSupplementTable(true);
          updateProduct(category, {benefitRows: []});
        }}>
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          Add benefits table
        </Button>
      );
    }
    return editor("What's covered", {
      defaultOpen: openSupplementTable,
      onRemove: () => {
        setOpenSupplementTable(false);
        updateProduct(category, {benefitRows: undefined, benefitsTableTitle: undefined});
      },
    });
  }

  const needsCore = !CORE_CATEGORIES.some((c) => productByCat.has(c));

  // One product inside a multi-product step card (Medical, Extra Protection): Add or remove in
  // the header, its editor below once added.
  function renderProductRow(category: ProductCategory, note?: ReactNode) {
    const product = productByCat.get(category);
    // Inside the Medical card, "ACA medical" tells it apart from the card and from limited medical.
    const label = category === 'medical' ? 'ACA medical' : CATEGORY_LABELS[category];
    return (
      <div key={category} className='overflow-hidden rounded-lg border'>
        <div className='flex items-center gap-2 border-b bg-muted/20 px-3 py-2'>
          <span className='flex-1 truncate text-sm font-medium'>{label}</span>
          {productLocked(category) ? (
            <span className='text-xs text-muted-foreground'>
              {product ? `Mirrored from ${sourceName}` : `Not offered by ${sourceName}`}
            </span>
          ) : product ? (
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7 text-destructive hover:text-destructive'
              aria-label='Remove product'
              onClick={() => removeProduct(category)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          ) : (
            <Button variant='outline' size='sm' onClick={() => addProduct(category)}>
              <Plus className='mr-1.5 h-3.5 w-3.5' />
              Add
            </Button>
          )}
        </div>
        {product && (
          <div className='p-3'>
            {note}
            {renderEditor(category)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        These are the steps of the enrollee flow. Medical or limited medical is required; add
        whichever other products this group offers.
      </p>

      <Field
        label='Pay period'
        hint='How often members pay. Enter each rate as the amount taken per pay period. Also sets the e123 billing period.'
      >
        <Select
          value={config.payPeriod ?? 'weekly'}
          onValueChange={(value) => {
            const next = value as PayPeriod;
            patch({payPeriod: next === 'weekly' ? undefined : next});
            patchE123({periodId: PAY_PERIODS[next].e123PeriodId});
          }}
        >
          <SelectTrigger className='w-48'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PAY_PERIODS).map(([value, period]) => (
              <SelectItem key={value} value={value}>
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <StepCard title='Medical' required>
        {needsCore && (
          <p className='mb-3 text-sm text-muted-foreground'>
            Add ACA medical, Limited medical, or both. At least one is required before this site can go
            live.
          </p>
        )}
        <div className='space-y-3'>
          {CORE_CATEGORIES.map((category) =>
            renderProductRow(
              category,
              category === 'limited-medical' && productByCat.has(category) && (
                <p className='mb-3 text-xs text-muted-foreground'>
                  {productByCat.has('medical')
                    ? 'Shown under ACA medical on the medical step.'
                    : 'With no ACA medical product, this is the medical step.'}
                </p>
              ),
            ),
          )}
        </div>
      </StepCard>

      {STANDALONE_SLOTS.map((category) => {
        const product = productByCat.get(category);
        const label = CATEGORY_LABELS[category];
        return (
          <StepCard
            key={category}
            title={label}
            onRemove={product && !productLocked(category) ? () => removeProduct(category) : undefined}
          >
            {product ? (
              renderEditor(category)
            ) : productLocked(category) ? (
              <p className='text-sm text-muted-foreground'>
                Mirrored from {sourceName}, which doesn’t offer {label.toLowerCase()}.
              </p>
            ) : (
              <EmptyBody label={label} onAdd={() => addProduct(category)} />
            )}
          </StepCard>
        );
      })}

      <StepCard title='Extra Protection'>
        <p className='mb-3 text-sm text-muted-foreground'>
          A single combined step. Add any of these optional products; each appears as a card
          members can add on their own.
        </p>
        <div className='space-y-3'>
          {PROTECTION_MEMBERS.map((category) => renderProductRow(category))}
        </div>
      </StepCard>
    </div>
  );
}

function StepCard({
  title,
  required,
  onRemove,
  children,
}: {
  title: string;
  required?: boolean;
  onRemove?: () => void;
  children: ReactNode;
}) {
  return (
    <div className='overflow-hidden rounded-lg border'>
      <div className='flex items-center gap-2 border-b bg-muted/30 px-3 py-2'>
        <span className='flex-1 truncate text-sm font-semibold'>{title}</span>
        <Badge variant={required ? 'secondary' : 'outline'} className='font-normal'>
          {required ? 'Required' : 'Optional'}
        </Badge>
        {onRemove && (
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 text-destructive hover:text-destructive'
            aria-label='Remove'
            onClick={onRemove}
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        )}
      </div>
      <div className='p-3'>{children}</div>
    </div>
  );
}

function EmptyBody({label, onAdd}: {label: string; onAdd: () => void}) {
  return (
    <div className='flex items-center justify-between gap-3'>
      <p className='text-sm text-muted-foreground'>{label} isn’t part of this site.</p>
      <Button variant='outline' size='sm' onClick={onAdd}>
        <Plus className='mr-1.5 h-3.5 w-3.5' />
        Add
      </Button>
    </div>
  );
}

// The member's opt-out treatment, folded from the two underlying fields
// (declinable + declineStyle) into one choice so the editor has a single control.
type OptOut = 'declined' | 'skipped' | 'none';

const OPT_OUT_OPTIONS: {value: OptOut; label: string; help: string}[] = [
  {
    value: 'declined',
    label: 'Decline (recorded)',
    help: 'Members can decline; the receipt records an explicit declination (e.g. medical).',
  },
  {
    value: 'skipped',
    label: 'Skip',
    help: 'Members can pass; shown softly as “skipped,” with no declination (e.g. dental).',
  },
  {
    value: 'none',
    label: 'No prompt',
    help: 'No decline prompt — members simply leave it unselected (e.g. a supplement).',
  },
];

function optOutOf(product: ProductConfig): OptOut {
  if (product.declinable === false) return 'none';
  return product.declineStyle === 'skipped' ? 'skipped' : 'declined';
}

function optOutPatch(value: OptOut): Partial<ProductConfig> {
  if (value === 'none') return {declinable: false, declineStyle: undefined};
  if (value === 'skipped') return {declinable: true, declineStyle: 'skipped'};
  return {declinable: true, declineStyle: undefined};
}

function ProductEditor({
  product,
  tierLocked,
  waiveLocked,
  badgeToggle,
  onChange,
}: {
  product: ProductConfig;
  tierLocked?: boolean;
  waiveLocked?: boolean;
  /** Extra Protection never shows the badge, so it gets no toggle. */
  badgeToggle?: boolean;
  onChange: (partial: Partial<ProductConfig>) => void;
}) {
  const optOut = waiveLocked ? 'declined' : optOutOf(product);
  const optHelp = OPT_OUT_OPTIONS.find((o) => o.value === optOut)?.help;
  return (
    <div className='space-y-4'>
      <TextField label='Label' value={product.label} onChange={(label) => onChange({label})} />
      <TextArea
        label='Description'
        rows={2}
        value={product.description}
        onChange={(description) => onChange({description})}
      />
      <TextField
        label='Step video URL'
        hint='Optional autoplay video at the top of this product step. Hand the raw file to engineering to host on Mux and paste the Mux URL here — avoid linking an unmanaged source.'
        value={product.videoUrl ?? ''}
        onChange={(v) => onChange({videoUrl: v || undefined})}
      />
      <TextField
        label='Video width (%)'
        hint='How wide the video is, centered. Blank or 100 = full width. Only applies when a video URL is set.'
        value={product.videoWidthPercent?.toString() ?? ''}
        onChange={(v) => {
          const n = Number(v);
          onChange({
            videoWidthPercent:
              v.trim() && Number.isFinite(n) ? Math.min(100, Math.max(10, n)) : undefined,
          });
        }}
      />

      <Field label='Waiving coverage'>
        <div className='mt-1 flex items-center gap-2'>
          <div className='inline-flex rounded-lg border bg-muted/40 p-0.5'>
            {OPT_OUT_OPTIONS.map((o) => {
              const selected = o.value === optOut;
              const disabled = waiveLocked && !selected;
              return (
                <button
                  key={o.value}
                  type='button'
                  disabled={disabled}
                  onClick={() => onChange(optOutPatch(o.value))}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm transition-colors',
                    selected
                      ? 'bg-white font-medium text-foreground shadow-[0_1px_2px_rgba(16,24,40,0.12)]'
                      : 'text-muted-foreground hover:text-foreground',
                    disabled && 'opacity-40 hover:text-muted-foreground',
                  )}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          {waiveLocked && (
            <span className='text-xs text-muted-foreground'>· required for medical</span>
          )}
        </div>
        <p className='mt-1.5 text-xs leading-relaxed text-muted-foreground'>{optHelp}</p>
      </Field>

      <div>
        <label className={cn('flex items-center gap-2 text-sm', tierLocked && 'text-muted-foreground')}>
          <input
            type='checkbox'
            checked={product.tierSelectable !== false}
            disabled={tierLocked}
            onChange={(e) => onChange({tierSelectable: e.target.checked ? undefined : false})}
          />
          Coverage tiers
          {tierLocked && <span className='text-xs'>· always on for medical</span>}
        </label>
        <p className='mt-1 pl-6 text-xs text-muted-foreground'>
          Prices vary by who’s covered — Employee, +Spouse, +Child(ren), Family.
        </p>
      </div>

      {/* Life insurance, or limited medical bundled with term life. */}
      {(product.category === 'life' || product.category === 'limited-medical') && (
        <div>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={!!product.collectBeneficiary}
              onChange={(e) => onChange({collectBeneficiary: e.target.checked || undefined})}
            />
            Collect a beneficiary
          </label>
          <p className='mt-1 pl-6 text-xs text-muted-foreground'>
            Members who choose this product must name a beneficiary at checkout.
          </p>
        </div>
      )}

      {badgeToggle && (
        <div>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={!product.hideLowestCostBadge}
              onChange={(e) => onChange({hideLowestCostBadge: e.target.checked ? undefined : true})}
            />
            Show “Lowest cost” badge
          </label>
          <p className='mt-1 pl-6 text-xs text-muted-foreground'>
            Marks the cheapest plan when there’s more than one.
          </p>
        </div>
      )}
    </div>
  );
}

// Reorder rows so same-category rows are contiguous, in category first-appearance
// order — matches how the enroll table groups them, so array index == display order.
function groupNormalize(rows: BenefitRow[]): BenefitRow[] {
  const order: string[] = [];
  for (const r of rows) if (!order.includes(r.category)) order.push(r.category);
  return order.flatMap((cat) => rows.filter((r) => r.category === cat));
}

// Editable version of the enroll "compare benefits" table: plan columns, rows
// grouped under category headers, reordered with up/down arrows (which move a row
// across the section boundary when it's first/last in its group).
function BenefitsTableEditor({
  heading,
  defaultOpen,
  rows,
  plans,
  onRowsChange,
  onRemove,
}: {
  heading: string;
  defaultOpen?: boolean;
  rows: BenefitRow[];
  plans: PlanConfig[];
  onRowsChange: (rows: BenefitRow[]) => void;
  onRemove?: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const data = groupNormalize(rows);

  const cols = `minmax(150px, 1.4fr) ${plans.map(() => 'minmax(130px, 1fr)').join(' ')} 6rem`;
  const minWidth = 280 + plans.length * 140;

  const usedCats = new Set(data.map((r) => r.category));
  const unusedCats = BENEFIT_CATEGORIES.filter((c) => !usedCats.has(c.value));

  const groups: {category: string; items: {row: BenefitRow; index: number}[]}[] = [];
  data.forEach((row, index) => {
    let g = groups.find((x) => x.category === row.category);
    if (!g) {
      g = {category: row.category, items: []};
      groups.push(g);
    }
    g.items.push({row, index});
  });

  function updateRow(i: number, partial: Partial<BenefitRow>) {
    onRowsChange(groupNormalize(data.map((r, idx) => (idx === i ? {...r, ...partial} : r))));
  }
  function setCell(i: number, planId: string, val: string) {
    onRowsChange(
      groupNormalize(
        data.map((r, idx) =>
          idx === i ? {...r, values: {...r.values, [planId]: val === '' ? null : val}} : r,
        ),
      ),
    );
  }
  function addRow(category: string) {
    onRowsChange(groupNormalize([...data, {category, label: '', values: {}}]));
  }
  function removeRow(i: number) {
    onRowsChange(data.filter((_, idx) => idx !== i));
  }
  function moveRow(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= data.length) return;
    const next = [...data];
    if (data[i].category === data[j].category) {
      // Same section — swap with the neighbour.
      [next[i], next[j]] = [next[j], next[i]];
    } else {
      // At a section boundary — move into the adjacent section (no swap).
      next[i] = {...data[i], category: data[j].category};
    }
    onRowsChange(groupNormalize(next));
  }

  return (
    <div className='rounded-lg border'>
      <div className='flex items-center gap-1 px-3 py-2'>
        {/* A div, not a <button>, so a mirrored (disabled) fieldset doesn't stop it expanding. */}
        <div
          role='button'
          tabIndex={0}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className='flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left'
        >
          <ChevronRight className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
          <span className='text-sm font-medium'>{heading}</span>
          <span className='text-xs text-muted-foreground'>
            {data.length} {data.length === 1 ? 'row' : 'rows'}
          </span>
        </div>
        {onRemove && (
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 shrink-0 text-destructive hover:text-destructive'
            aria-label='Remove'
            onClick={onRemove}
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        )}
      </div>

      {open && (
        <div className='border-t p-3'>
          {plans.length === 0 ? (
            <p className='text-sm text-muted-foreground'>Add plans to build the comparison table.</p>
          ) : (
            <div className='overflow-x-auto'>
              <div style={{minWidth}} className='space-y-1'>
                <div style={{gridTemplateColumns: cols}} className='grid gap-2 px-1 pb-1'>
                  <span className='text-[11px] font-bold uppercase tracking-wide text-muted-foreground'>
                    Benefit
                  </span>
                  {plans.map((pl) => (
                    <span key={pl.id} className='flex items-center gap-1.5 text-[11px] font-semibold'>
                      <span className='h-2 w-2 shrink-0 rounded-full' style={{backgroundColor: pl.accentColor}} />
                      <span className='truncate'>{pl.name || 'Untitled plan'}</span>
                    </span>
                  ))}
                  <span />
                </div>

                {groups.map((group) => (
                  <div key={group.category} className='space-y-1'>
                    <div className='flex items-center gap-1.5 pt-2'>
                      <span
                        className='h-2 w-2 shrink-0 rounded-full'
                        style={{backgroundColor: BENEFIT_CATEGORY_COLORS[group.category] ?? '#94a3b8'}}
                      />
                      <span
                        className='text-[11px] font-bold uppercase tracking-wide'
                        style={{color: BENEFIT_CATEGORY_COLORS[group.category] ?? '#94a3b8'}}
                      >
                        {BENEFIT_CATEGORY_LABELS[group.category] ?? group.category}
                      </span>
                    </div>
                    {group.items.map(({row, index}) => (
                      <BenefitRow
                        key={index}
                        cols={cols}
                        row={row}
                        plans={plans}
                        isFirst={index === 0}
                        isLast={index === data.length - 1}
                        onLabel={(label) => updateRow(index, {label})}
                        onCell={(planId, val) => setCell(index, planId, val)}
                        onMove={(dir) => moveRow(index, dir)}
                        onRemove={() => removeRow(index)}
                      />
                    ))}
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-7 px-2 text-xs text-muted-foreground'
                      onClick={() => addRow(group.category)}
                    >
                      <Plus className='mr-1 h-3 w-3' /> Add row
                    </Button>
                  </div>
                ))}

                {unusedCats.length > 0 && (
                  <div className='pt-3'>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='outline' size='sm' className='h-8 text-xs'>
                          <Plus className='mr-1 h-3.5 w-3.5' /> Add section
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='start'>
                        {unusedCats.map((c) => (
                          <DropdownMenuItem key={c.value} onSelect={() => addRow(c.value)}>
                            <span
                              className='mr-2 h-2 w-2 shrink-0 rounded-full'
                              style={{backgroundColor: BENEFIT_CATEGORY_COLORS[c.value] ?? '#94a3b8'}}
                            />
                            {c.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BenefitRow({
  cols,
  row,
  plans,
  isFirst,
  isLast,
  onLabel,
  onCell,
  onMove,
  onRemove,
}: {
  cols: string;
  row: BenefitRow;
  plans: PlanConfig[];
  isFirst: boolean;
  isLast: boolean;
  onLabel: (v: string) => void;
  onCell: (planId: string, v: string) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{gridTemplateColumns: cols}}
      className='grid items-center gap-2 rounded-md px-1 py-0.5 hover:bg-muted/40'
    >
      <Input
        className='h-8'
        placeholder='Benefit label'
        value={row.label}
        onChange={(e) => onLabel(e.target.value)}
      />
      {plans.map((pl) => (
        <Input
          key={pl.id}
          className='h-8'
          placeholder='—'
          value={row.values[pl.id] ?? ''}
          onChange={(e) => onCell(pl.id, e.target.value)}
        />
      ))}
      <div className='flex items-center justify-end'>
        <Button variant='ghost' size='icon' className='h-7 w-7' aria-label='Move up' disabled={isFirst} onClick={() => onMove(-1)}>
          <ChevronUp className='h-4 w-4' />
        </Button>
        <Button variant='ghost' size='icon' className='h-7 w-7' aria-label='Move down' disabled={isLast} onClick={() => onMove(1)}>
          <ChevronDown className='h-4 w-4' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7 text-destructive hover:text-destructive'
          onClick={onRemove}
        >
          <Trash2 className='h-4 w-4' />
        </Button>
      </div>
    </div>
  );
}

function PlanEditor({
  plan,
  tiered,
  defaultOpen,
  isFirst,
  isLast,
  groupSlug,
  isNew,
  onChange,
  onRemove,
  onMove,
  locked = false,
  ratesLocked = false,
  payPeriod,
  showDetailRows = false,
  detailRowSources = [],
}: {
  plan: PlanConfig;
  payPeriod: PayPeriodInfo;
  /** Extra Protection plans show "What's covered" rows on the site; other plans don't. */
  showDetailRows?: boolean;
  detailRowSources?: RankedDetailRowDefault[];
  tiered: boolean;
  defaultOpen?: boolean;
  isFirst: boolean;
  isLast: boolean;
  groupSlug: string;
  isNew: boolean;
  onChange: (partial: Partial<PlanConfig>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  /** Mirrored product: everything but the expand toggle (and rates, unless `ratesLocked`) is read-only. */
  locked?: boolean;
  ratesLocked?: boolean;
}) {
  const lockedSet = cn('m-0 min-w-0 space-y-4 border-0 p-0', locked && 'opacity-60');
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className='rounded-lg border bg-white'>
      <div className='flex items-center gap-1 px-3 py-2'>
        <button
          type='button'
          onClick={() => setOpen((v) => !v)}
          className='flex min-w-0 flex-1 items-center gap-2 text-left'
        >
          <ChevronRight
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
          />
          <span className='h-2.5 w-2.5 shrink-0 rounded-full' style={{backgroundColor: plan.accentColor}} />
          <span className='truncate text-sm font-medium'>{plan.name || 'Untitled plan'}</span>
          <span className='shrink-0 text-xs text-muted-foreground'>
            ${(plan.rates.employee ?? 0).toFixed(2)}
            {payPeriod.priceSuffix}
          </span>
        </button>
        <Button variant='ghost' size='icon' className='h-7 w-7' aria-label='Move up' disabled={isFirst || locked} onClick={() => onMove(-1)}>
          <ChevronUp className='h-4 w-4' />
        </Button>
        <Button variant='ghost' size='icon' className='h-7 w-7' aria-label='Move down' disabled={isLast || locked} onClick={() => onMove(1)}>
          <ChevronDown className='h-4 w-4' />
        </Button>
        <Button variant='ghost' size='icon' className='h-7 w-7 text-destructive hover:text-destructive' disabled={locked} onClick={onRemove}>
          <Trash2 className='h-4 w-4' />
        </Button>
      </div>
      {open && (
      <div className='space-y-4 border-t p-3'>
        <fieldset disabled={locked} className={lockedSet}>
          <TextField label='Name' value={plan.name} onChange={(name) => onChange({name})} />
          <TextField label='Tagline' value={plan.tagline} onChange={(v) => onChange({tagline: v || undefined})} />
        </fieldset>
        <fieldset disabled={ratesLocked} className={cn('m-0 min-w-0 border-0 p-0', ratesLocked && 'opacity-60')}>
          <Field
            label={`${payPeriod.label} rates`}
            hintTop
            hint={tiered ? undefined : 'Employee-only pricing — other tiers are unused.'}
          >
            <div className='grid grid-cols-2 gap-2'>
              {TIERS.map((tier) => {
                const disabled = !tiered && tier !== 'employee';
                return (
                  <label
                    key={tier}
                    className={cn('flex items-center gap-2 text-sm', disabled && 'opacity-50')}
                  >
                    <span className='w-28 shrink-0 text-xs text-muted-foreground'>{COVERAGE_TIER_LABELS[tier]}</span>
                    <span className='text-muted-foreground'>$</span>
                    {/* Blur on wheel so scrolling moves the page instead of stepping the rate. */}
                    <Input
                      type='number'
                      min={0}
                      step='0.01'
                      disabled={disabled}
                      value={plan.rates[tier] ?? 0}
                      onWheel={(e) => e.currentTarget.blur()}
                      onChange={(e) => {
                        if (/\.\d{3,}/.test(e.target.value)) return; // cents only
                        onChange({rates: {...plan.rates, [tier]: Number(e.target.value)}});
                      }}
                    />
                  </label>
                );
              })}
            </div>
          </Field>
        </fieldset>
        <fieldset disabled={locked} className={lockedSet}>
          <StringListEditor
            label='Highlights'
            items={plan.highlights ?? []}
            onChange={(highlights) => onChange({highlights})}
          />
          <div className='flex gap-3'>
            <div className='flex-1'>
              <TextField label='Network' value={plan.network} onChange={(v) => onChange({network: v || undefined})} />
            </div>
            <div className='flex-1'>
              <Field label='Benefits summary (PDF)'>
                <PdfUploadField
                  groupSlug={groupSlug}
                  isNew={isNew}
                  value={plan.sbcUrl}
                  onChange={(sbcUrl) => onChange({sbcUrl})}
                />
              </Field>
            </div>
          </div>
          <ColorSwatchField
            label='Accent color'
            value={plan.accentColor}
            onChange={(accentColor) => onChange({accentColor})}
            colors={PLAN_ACCENT_COLOR_POOL}
          />
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={!!plan.limitedBenefit}
              onChange={(e) => onChange({limitedBenefit: e.target.checked || undefined})}
            />
            Limited benefit (not ACA/MEC)
          </label>
          {showDetailRows && (
            <DetailRowsEditor
              rows={plan.detailRows ?? []}
              sources={detailRowSources}
              onChange={(rows) => onChange({detailRows: rows.length ? rows : undefined})}
            />
          )}
        </fieldset>
      </div>
      )}
    </div>
  );
}

const DETAIL_ROW_MATCH_LABEL = {product: 'Same e123 product', name: 'Same plan name'} as const;

function DetailRowsEditor({
  rows,
  sources,
  onChange,
}: {
  rows: DetailRow[];
  sources: RankedDetailRowDefault[];
  onChange: (rows: DetailRow[]) => void;
}) {
  const [pending, setPending] = useState<RankedDetailRowDefault | null>(null);
  const update = (i: number, partial: Partial<DetailRow>) =>
    onChange(rows.map((row, idx) => (idx === i ? {...row, ...partial} : row)));
  const copy = (source: RankedDetailRowDefault) => onChange(source.rows.map((row) => ({...row})));
  return (
    <Field label="What's covered" hint='Shown under “See full details” on this plan’s card.'>
      <div className='space-y-2'>
        {rows.map((row, i) => (
          <div key={i} className='flex items-center gap-2'>
            <Input placeholder='Benefit' value={row.label} onChange={(e) => update(i, {label: e.target.value})} />
            <Input placeholder='Amount' value={row.value} onChange={(e) => update(i, {value: e.target.value})} />
            <Button
              variant='ghost'
              size='icon'
              aria-label='Remove'
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ))}
        <div className='flex flex-wrap gap-2'>
          <Button variant='outline' size='sm' onClick={() => onChange([...rows, {label: '', value: ''}])}>
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add row
          </Button>
          {/* Non-modal: a modal menu that opens the confirm dialog leaves the page click-locked after it closes. */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm' disabled={sources.length === 0}>
                Copy from...
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' className='max-h-80 overflow-y-auto'>
              {sources.map((source) => (
                <DropdownMenuItem
                  key={`${source.sourceSlug}:${source.planId}`}
                  onSelect={() => (rows.length ? setPending(source) : copy(source))}
                  className='flex flex-col items-start gap-0.5'
                >
                  <span>
                    {source.sourceSlug} · {source.planName} ({source.rows.length} rows)
                  </span>
                  {source.match && (
                    <span className='text-xs text-muted-foreground'>{DETAIL_ROW_MATCH_LABEL[source.match]}</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the current rows?</AlertDialogTitle>
            <AlertDialogDescription>
              This plan’s {rows.length} rows will be replaced with {pending?.rows.length} rows from{' '}
              {pending?.sourceSlug} · {pending?.planName}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep current rows</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending) copy(pending);
                setPending(null);
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Field>
  );
}
